import { useEffect, useState } from 'react';
import { HiX, HiTruck, HiUser, HiCurrencyDollar, HiPencil } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { ordersAPI } from '../services/api';
import { mensajeDeError } from '../utils/apiError';
import { notify } from '../utils/notify';

const money = (value, currency = 'USD') =>
    value === null || value === undefined
        ? '—'
        : new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value));

/**
 * Order detail. Shows only what the order actually holds — no filler rows for
 * fields the order does not have.
 */
export default function OrderDetailModal({ isOpen, orderId, onClose }) {
    const { t } = useTranslation();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // El seguimiento se podia enseñar pero no escribir: el endpoint y el metodo
    // del cliente existian desde el principio y no los llamaba ninguna
    // pantalla, asi que esta ficha decia «todavia sin seguimiento» para siempre.
    const [editandoEnvio, setEditandoEnvio] = useState(false);
    const [numero, setNumero] = useState('');
    const [transportista, setTransportista] = useState('');
    const [guardando, setGuardando] = useState(false);

    const guardarSeguimiento = async (e) => {
        e.preventDefault();
        if (!numero.trim()) return;

        setGuardando(true);
        try {
            const res = await ordersAPI.updateTracking(order.id, {
                trackingNumber: numero.trim(),
                ...(transportista.trim() ? { trackingCarrier: transportista.trim() } : {})
            });
            setOrder(res.data.data.order);
            setEditandoEnvio(false);
            notify.success(t('orders.tracking_saved'));
        } catch (err) {
            notify.error(mensajeDeError(err, t('orders.tracking_failed')));
        } finally {
            setGuardando(false);
        }
    };

    const abrirEdicion = () => {
        setNumero(order?.tracking_number || '');
        setTransportista(order?.tracking_carrier || '');
        setEditandoEnvio(true);
    };

    useEffect(() => {
        if (!isOpen || !orderId) return;

        let cancelled = false;
        setLoading(true);
        setError(null);

        ordersAPI
            .getById(orderId)
            .then((response) => {
                if (!cancelled) setOrder(response.data.data.order);
            })
            .catch((err) => {
                if (!cancelled) setError(mensajeDeError(err, t('orders.detail_failed')));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, orderId, t]);

    if (!isOpen) return null;

    const address = order?.shipping_address;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>

            <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white dark:bg-slate-800 rounded-2xl shadow-xl flex flex-col">
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                            {order?.order_number ? `#${order.order_number}` : t('orders.detail_title')}
                        </h2>
                        {order?.store?.store_name && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">{order.store.store_name}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <HiX className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 overflow-y-auto space-y-6">
                    {loading && <p className="text-slate-500">{t('common.loading')}</p>}
                    {error && <p className="text-red-500">{error}</p>}

                    {order && !loading && (
                        <>
                            <section className="space-y-2">
                                <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                                    <HiUser className="w-5 h-5 text-slate-400" />
                                    {t('orders.customer')}
                                </h3>
                                <dl className="text-sm space-y-1">
                                    {order.customer_name && (
                                        <div className="flex gap-2">
                                            <dt className="text-slate-500">{t('orders.customer_name')}:</dt>
                                            <dd className="text-slate-900 dark:text-white">{order.customer_name}</dd>
                                        </div>
                                    )}
                                    {order.customer_email && (
                                        <div className="flex gap-2">
                                            <dt className="text-slate-500">{t('orders.customer_email')}:</dt>
                                            <dd className="text-slate-900 dark:text-white">{order.customer_email}</dd>
                                        </div>
                                    )}
                                    {address && (
                                        <div className="flex gap-2">
                                            <dt className="text-slate-500">{t('orders.shipping_address')}:</dt>
                                            <dd className="text-slate-900 dark:text-white">
                                                {[address.address1, address.city, address.province, address.country, address.zip]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            </section>

                            <section className="space-y-2">
                                <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                                    <HiCurrencyDollar className="w-5 h-5 text-slate-400" />
                                    {t('orders.amounts')}
                                </h3>
                                <dl className="text-sm space-y-1">
                                    <div className="flex justify-between">
                                        <dt className="text-slate-500">{t('orders.total')}</dt>
                                        <dd className="text-slate-900 dark:text-white font-medium">
                                            {money(order.total_amount, order.currency)}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-slate-500">{t('orders.cost')}</dt>
                                        <dd className="text-slate-900 dark:text-white">{money(order.cost_of_goods, order.currency)}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-slate-500">{t('orders.profit')}</dt>
                                        <dd className="text-green-600 dark:text-green-400 font-medium">
                                            {money(order.profit, order.currency)}
                                        </dd>
                                    </div>
                                </dl>
                            </section>

                            <section className="space-y-2">
                                <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                                    <HiTruck className="w-5 h-5 text-slate-400" />
                                    {t('orders.shipping')}
                                </h3>
                                {editandoEnvio ? (
                                    <form onSubmit={guardarSeguimiento} className="space-y-2">
                                        <input
                                            autoFocus
                                            value={numero}
                                            onChange={(e) => setNumero(e.target.value)}
                                            placeholder={t('orders.tracking_number')}
                                            className="input w-full"
                                        />
                                        <input
                                            value={transportista}
                                            onChange={(e) => setTransportista(e.target.value)}
                                            placeholder={t('orders.tracking_carrier')}
                                            className="input w-full"
                                        />
                                        <p className="text-xs text-slate-500">{t('orders.tracking_hint')}</p>
                                        <div className="flex gap-2">
                                            <button
                                                type="submit"
                                                disabled={guardando || !numero.trim()}
                                                className="btn-primary btn-sm"
                                            >
                                                {t('orders.tracking_save')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditandoEnvio(false)}
                                                className="btn-secondary btn-sm"
                                            >
                                                {t('orders.cancel')}
                                            </button>
                                        </div>
                                    </form>
                                ) : order.tracking_number ? (
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm text-slate-900 dark:text-white">
                                            {order.tracking_carrier ? `${order.tracking_carrier} · ` : ''}
                                            {order.tracking_url ? (
                                                <a
                                                    href={order.tracking_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary-600 dark:text-primary-400 underline"
                                                >
                                                    {order.tracking_number}
                                                </a>
                                            ) : (
                                                order.tracking_number
                                            )}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={abrirEdicion}
                                            className="icon-edit"
                                            title={t('orders.edit_tracking')}
                                        >
                                            <HiPencil className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-sm text-slate-500">{t('orders.no_tracking_yet')}</p>
                                        <button
                                            type="button"
                                            onClick={abrirEdicion}
                                            className="btn-secondary btn-sm"
                                        >
                                            {t('orders.add_tracking')}
                                        </button>
                                    </div>
                                )}
                                {order.supplier_order_id && (
                                    <p className="text-sm text-slate-500">
                                        {t('orders.supplier_order')}: {order.supplier_order_id}
                                    </p>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
